import React from "react";
import { Helmet } from "react-helmet";
import pathway_book from "../assets/pathway_book/pathway_book_pg1.webp";

export default function OrderCurriculum() {
  return (
    <div className="bg-gradient-to-b from-white via-blue-50/30 to-white font-nunito">
      <Helmet>
        <title>Order Curriculum | Pathway Resource Center</title>
        <meta
          name="description"
          content="Order bible-based curriculum and lessons for teaching kids life skills and risk avoidance. Lessons cover topics like goal-setting, social media risks, abstinence and drug avoidance, and relationship skills."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#457B9D] via-[#5a8fb0] to-[#457B9D] py-20">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -left-20 -top-20 h-80 w-80 rounded-full bg-white blur-3xl"></div>
          <div className="absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-white blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <div className="mb-6 inline-block rounded-full bg-white/20 px-6 py-2 backdrop-blur-sm">
            <span className="text-sm font-semibold uppercase tracking-wide text-white">
              Invest in Young Lives
            </span>
          </div>

          <h1 className="mb-6 text-4xl font-bold leading-tight text-white md:text-5xl lg:text-6xl">
            Order Pathway Curriculum
          </h1>

          <p className="mx-auto max-w-2xl text-lg leading-relaxed text-white/95 md:text-xl">
            Equip the next generation with life skills and Biblical wisdom through our comprehensive workbook program
          </p>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg
            viewBox="0 0 1440 120"
            className="w-full"
            preserveAspectRatio="none"
            style={{ height: '80px' }}
          >
            <path
              d="M0,64 C360,120 720,0 1440,64 L1440,120 L0,120 Z"
              fill="white"
              opacity="0.3"
            />
            <path
              d="M0,80 C360,20 720,100 1440,80 L1440,120 L0,120 Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Left Column - Image */}
            <div className="flex items-center justify-center">
              <div className="relative">
                {/* Decorative circles */}
                <div className="absolute -left-6 -top-6 h-32 w-32 rounded-full bg-blue-100 opacity-50 blur-2xl"></div>
                <div className="absolute -bottom-6 -right-6 h-40 w-40 rounded-full bg-pink-100 opacity-50 blur-2xl"></div>

                {/* Book Image Container */}
                <div
                  className="relative overflow-hidden rounded-3xl bg-white p-8 shadow-2xl transition-all duration-500 hover:scale-105 hover:shadow-3xl"
                  style={{
                    animation: 'fadeInUp 0.6s ease-out both',
                  }}
                >
                  <img
                    src={pathway_book}
                    alt="Pathway Life Skills Curriculum Workbook"
                    className="h-auto w-full max-w-md rounded-xl object-contain drop-shadow-xl"
                    loading="lazy"
                  />

                  {/* Border accent */}
                  <div className="absolute inset-0 rounded-3xl border-4 border-[#457B9D]/10"></div>
                </div>
              </div>
            </div>

            {/* Right Column - Information & CTA */}
            <div className="flex flex-col justify-center space-y-8">
              {/* Donation Information Card */}
              <div
                className="rounded-2xl bg-gradient-to-br from-blue-50 to-white p-8 shadow-lg transition-all duration-300 hover:shadow-xl"
                style={{
                  animation: 'fadeInUp 0.6s ease-out 0.2s both',
                }}
              >
                <div className="mb-4 inline-flex rounded-full bg-[#457B9D]/10 p-3">
                  <svg
                    className="h-8 w-8 text-[#457B9D]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
                <h2 className="mb-4 text-2xl font-bold text-gray-800">
                  Suggested Donation: $10 per Workbook
                </h2>
                <p className="mb-6 leading-relaxed text-gray-700">
                  If you are unable to donate the suggested amount, we still want you to have one. Simply put in the amount you are able to donate or contact us via email at{" "}
                  <a
                    href="mailto:info@PathwayTXK.org"
                    className="font-semibold text-[#457B9D] transition-colors hover:text-[#365f7a]"
                  >
                    info@PathwayTXK.org
                  </a>
                </p>
              </div>

              {/* Give More Card */}
              <div
                className="rounded-2xl bg-gradient-to-br from-pink-50 to-white p-8 shadow-lg transition-all duration-300 hover:shadow-xl"
                style={{
                  animation: 'fadeInUp 0.6s ease-out 0.3s both',
                }}
              >
                <div className="mb-4 inline-flex rounded-full bg-pink-100 p-3">
                  <svg
                    className="h-8 w-8 text-[#457B9D]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                </div>
                <h2 className="mb-4 text-2xl font-bold text-gray-800">
                  Help Others Access This Resource
                </h2>
                <p className="leading-relaxed text-gray-700">
                  If you are able to contribute more than the suggested donation of $10 per book, your donation will be used to cover the costs of workbooks for individuals or youth groups who are not able to donate at this time. Your contribution will also be used for the production of future curriculum and programs offered by Pathway.
                </p>
              </div>

              {/* Primary CTA Button */}
              <div
                className="text-center"
                style={{
                  animation: 'fadeInUp 0.6s ease-out 0.4s both',
                }}
              >
                <a
                  href="https://engage.suran.com/pathwayresourcecenter/s/give/gift/80c96cabadbd4f9"
                  className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-[#457B9D] to-[#5a8fb0] px-10 py-5 text-lg font-bold text-white shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                    />
                  </svg>
                  Order Workbooks Now
                  <svg
                    className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Order Section */}
      <section className="bg-gradient-to-b from-white to-blue-50 py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="rounded-3xl bg-white p-8 shadow-xl md:p-12">
            <div className="mb-8 text-center">
              <div className="mb-4 inline-flex rounded-full bg-blue-100 p-4">
                <svg
                  className="h-10 w-10 text-[#457B9D]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h2 className="mb-4 text-3xl font-bold text-gray-800">
                How to Order
              </h2>
            </div>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#457B9D] text-lg font-bold text-white">
                    1
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="mb-2 text-xl font-semibold text-gray-800">
                    Click "Order Workbooks Now"
                  </h3>
                  <p className="leading-relaxed text-gray-700">
                    Use the button above to access our secure donation platform
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#457B9D] text-lg font-bold text-white">
                    2
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="mb-2 text-xl font-semibold text-gray-800">
                    Select "Donation for Workbook"
                  </h3>
                  <p className="leading-relaxed text-gray-700">
                    Choose this designation when making your donation
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#457B9D] text-lg font-bold text-white">
                    3
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="mb-2 text-xl font-semibold text-gray-800">
                    Add Number of Workbooks in Notes
                  </h3>
                  <p className="leading-relaxed text-gray-700">
                    Please be sure to put the number of workbooks you are ordering in the note section
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-xl bg-blue-50 p-6 text-center">
              <p className="text-gray-700">
                Questions? Email us at{" "}
                <a
                  href="mailto:info@PathwayTXK.org"
                  className="font-semibold text-[#457B9D] transition-colors hover:text-[#365f7a]"
                >
                  info@PathwayTXK.org
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#457B9D] to-[#365f7a] py-16">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -left-20 top-0 h-64 w-64 rounded-full bg-white blur-3xl"></div>
          <div className="absolute -right-20 bottom-0 h-64 w-64 rounded-full bg-white blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <h3 className="mb-6 text-3xl font-bold text-white md:text-4xl">
            Need More Information?
          </h3>
          <p className="mb-8 text-xl leading-relaxed text-blue-50">
            We're here to help answer any questions about our curriculum and how it can benefit your youth program
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
            <a
              href="https://engage.suran.com/pathwayresourcecenter/s/give/gift/80c96cabadbd4f9"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-8 py-4 text-lg font-bold text-[#457B9D] shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                />
              </svg>
              Order Now
            </a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white bg-transparent px-8 py-4 text-lg font-bold text-white transition-all duration-300 hover:bg-white hover:text-[#457B9D]"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Get In Touch
            </a>
          </div>
        </div>
      </section>

      {/* Animation Keyframes */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
